function cleanText(value) {
  return String(value === null || value === undefined ? '' : value).replace(/\s+/g, ' ').trim()
}

function names(work) {
  return (work && work.authors ? work.authors : []).map(item => cleanText(item && item.name)).filter(Boolean)
}

function sourceParts(work) {
  const source = cleanText(work && work.sourceName)
  const year = work && work.publicationYear ? String(work.publicationYear) : ''
  const biblio = work && work.extra && work.extra.biblio ? work.extra.biblio : {}
  const volume = cleanText(biblio.volume)
  const issue = cleanText(biblio.issue)
  const firstPage = cleanText(biblio.firstPage)
  const lastPage = cleanText(biblio.lastPage)
  const pages = pageRange(firstPage, lastPage)
  return { source, year, volume, issue, firstPage, lastPage, pages }
}

function pageRange(firstPage, lastPage) {
  const first = cleanText(firstPage)
  const last = cleanText(lastPage)
  if (first && last && first !== last) {
    return `${first}-${last}`
  }
  return first || last
}

function volumeIssue(volume, issue) {
  const vol = cleanText(volume)
  const iss = cleanText(issue)
  if (vol && iss) {
    return `${vol}(${iss})`
  }
  return vol || (iss ? `(${iss})` : '')
}

function sourceWithBiblio(work, style) {
  const { source, year, volume, issue, pages } = sourceParts(work)
  if (style === 'gbt7714') {
    const volumeText = volumeIssue(volume, issue)
    const issuePart = volumeText || ''
    const pagePart = pages ? `: ${pages}` : ''
    const yearPart = year ? `${year}` : ''
    const suffix = [yearPart, `${issuePart}${pagePart}`.trim()].filter(Boolean).join(', ')
    return [source, suffix].filter(Boolean).join(', ')
  }
  const volumeText = volumeIssue(volume, issue)
  if (style === 'apa') {
    return [source, volumeText, pages].filter(Boolean).join(', ')
  }
  if (style === 'mla') {
    const parts = []
    if (source) parts.push(`${source},`)
    if (volume) parts.push(`vol. ${volume},`)
    if (issue) parts.push(`no. ${issue},`)
    if (year) parts.push(`${year},`)
    if (pages) parts.push(`pp. ${pages}.`)
    return parts.join(' ')
  }
  return [source, volumeText, pages].filter(Boolean).join(', ')
}

function doiUrl(work) {
  return work && work.doi ? `https://doi.org/${work.doi}` : ''
}

function firstProviderRef(work) {
  return work && work.providerRefs && work.providerRefs.length ? work.providerRefs[0] : null
}

function firstProviderUrl(work) {
  const ref = firstProviderRef(work)
  return ref && ref.url ? ref.url : ''
}

function urlFor(work) {
  return cleanText((work && work.landingPageUrl) || doiUrl(work) || firstProviderUrl(work))
}

function authorsText(work, limit, separator, etAlText) {
  const list = names(work)
  if (!list.length) {
    return ''
  }
  const max = limit || list.length
  if (list.length > max) {
    return `${list.slice(0, max).join(separator || ', ')}${etAlText || ', et al.'}`
  }
  return list.join(separator || ', ')
}

function sentenceEnd(value) {
  const text = cleanText(value)
  if (!text) {
    return ''
  }
  return /[.!?。！？]$/.test(text) ? text : `${text}.`
}

export function citationKey(work) {
  const firstAuthor = names(work)[0] || 'work'
  const authorKey = firstAuthor.split(/\s+/).slice(-1)[0].replace(/[^a-z0-9]/gi, '') || 'work'
  const year = work && work.publicationYear ? work.publicationYear : 'nodate'
  const ref = firstProviderRef(work)
  const id = ((work && (work.doi || work.workKey)) || (ref && ref.providerId) || '').replace(/[^a-z0-9]/gi, '').slice(-6)
  return `${authorKey}${year}${id}`.replace(/[^a-z0-9]/gi, '').replace(/^\d+/, 'work')
}

export function bibTexType(type) {
  const typeMap = {
    book: 'book',
    'book-chapter': 'incollection',
    'proceedings-article': 'inproceedings',
    'conference-paper': 'inproceedings',
    dataset: 'misc'
  }
  return typeMap[type] || 'article'
}

export function risType(type) {
  const typeMap = {
    book: 'BOOK',
    'book-chapter': 'CHAP',
    'proceedings-article': 'CPAPER',
    'conference-paper': 'CPAPER',
    preprint: 'GEN'
  }
  return typeMap[type] || 'JOUR'
}

export function toBibTex(work) {
  const fields = [
    ['title', work && work.title],
    ['author', names(work).join(' and ')],
    ['journal', work && work.sourceName],
    ['volume', sourceParts(work).volume],
    ['number', sourceParts(work).issue],
    ['pages', sourceParts(work).pages],
    ['year', work && work.publicationYear],
    ['doi', work && work.doi],
    ['url', urlFor(work)]
  ].filter(([, value]) => cleanText(value))
  const body = fields.map(([field, value]) => `  ${field} = {${cleanText(value).replace(/[{}]/g, ' ')}}`).join(',\n')
  return `@${bibTexType(work && work.type)}{${citationKey(work)},\n${body}\n}`
}

export function toRis(work) {
  const lines = [`TY  - ${risType(work && work.type)}`]
  if (work && work.title) lines.push(`TI  - ${work.title}`)
  names(work).forEach(author => lines.push(`AU  - ${author}`))
  if (work && work.publicationYear) lines.push(`PY  - ${work.publicationYear}`)
  if (work && work.sourceName) lines.push(`T2  - ${work.sourceName}`)
  const biblio = sourceParts(work)
  if (biblio.volume) lines.push(`VL  - ${biblio.volume}`)
  if (biblio.issue) lines.push(`IS  - ${biblio.issue}`)
  if (biblio.firstPage) lines.push(`SP  - ${biblio.firstPage}`)
  if (biblio.lastPage && biblio.lastPage !== biblio.firstPage) lines.push(`EP  - ${biblio.lastPage}`)
  if (work && work.doi) lines.push(`DO  - ${work.doi}`)
  const url = urlFor(work)
  if (url) lines.push(`UR  - ${url}`)
  lines.push('ER  -')
  return lines.join('\n')
}

export function toGbt7714(work) {
  const author = authorsText(work, 3, ', ', ', 等')
  const title = cleanText(work && work.title)
  const type = work && (work.type === 'book' || work.type === 'book-chapter') ? 'M' : 'J'
  const sourceText = sourceWithBiblio(work, 'gbt7714')
  const doi = cleanText(work && work.doi)
  const url = urlFor(work)
  const parts = []
  if (author) parts.push(author)
  if (title) parts.push(`${title}[${type}]`)
  if (sourceText) parts.push(sourceText)
  if (doi) parts.push(`DOI: ${doi}`)
  if (!doi && url) parts.push(url)
  return sentenceEnd(parts.join('. '))
}

export function toApa(work) {
  const author = authorsText(work, 20, ', ', ', et al.')
  const year = work && work.publicationYear ? work.publicationYear : 'n.d.'
  const title = cleanText(work && work.title)
  const source = sourceWithBiblio(work, 'apa')
  const doi = cleanText(work && work.doi)
  const url = urlFor(work)
  const parts = []
  if (author) parts.push(`${author} (${year}).`)
  else parts.push(`(${year}).`)
  if (title) parts.push(sentenceEnd(title))
  if (source) parts.push(sentenceEnd(source))
  if (doi) parts.push(`https://doi.org/${doi}`)
  else if (url) parts.push(url)
  return parts.join(' ')
}

export function toMla(work) {
  const author = authorsText(work, 3, ', ', ', et al.')
  const title = cleanText(work && work.title)
  const source = sourceWithBiblio(work, 'mla')
  const url = urlFor(work)
  const parts = []
  if (author) parts.push(`${author}.`)
  if (title) parts.push(`"${title}."`)
  if (source) parts.push(source)
  if (url) parts.push(url)
  return parts.join(' ')
}

export function toCsv(works, formatType) {
  const headers = ['Title', 'Authors', 'Year', 'Source', 'Volume', 'Issue', 'Pages', 'Type', 'Language', 'Citations', 'OA', 'OA Status', 'DOI', 'PDF URL', 'Landing URL']
  const rows = (works || []).map(work => [
    work.title || '',
    names(work).join('; '),
    work.publicationYear || '',
    work.sourceName || '',
    sourceParts(work).volume,
    sourceParts(work).issue,
    sourceParts(work).pages,
    formatType ? formatType(work.type) : (work.type || ''),
    work.language || '',
    work.citedByCount || 0,
    work.isOa ? 'yes' : 'no',
    work.oaStatus || '',
    work.doi || '',
    work.pdfUrl || '',
    work.landingPageUrl || ''
  ])
  return [headers].concat(rows).map(row => row.map(csvCell).join(',')).join('\n')
}

export function formatCitation(work, format) {
  const map = {
    gbt7714: toGbt7714,
    apa: toApa,
    mla: toMla,
    bibtex: toBibTex,
    ris: toRis
  }
  const formatter = map[format] || toGbt7714
  return formatter(work)
}

export function formatWorks(works, format, formatType) {
  if (format === 'csv') {
    return toCsv(works, formatType)
  }
  return (works || []).map(work => formatCitation(work, format)).join('\n\n')
}

function csvCell(value) {
  const text = String(value === null || value === undefined ? '' : value)
  return `"${text.replace(/"/g, '""')}"`
}
